import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import {
  STEP_UP_CODES,
  startStepUp,
  verifyStepUp,
  type StepUpAction,
  type StepUpMethod,
  type StepUpParams,
} from '../api/step-up.ts';
import { messageFor, type SheetSeed } from '../lib/step-up-attempt.ts';
import { biometricLabel, usePlatform, type BiometricState } from '../platform/index.ts';

export interface StepUpSheetProps {
  readonly action: StepUpAction;
  readonly params: StepUpParams;
  // One line saying what is being confirmed, e.g. "Send 10 USDT to TAbc…".
  readonly summary: string;
  // What the sheet opens with. The silent attempt already started the
  // ceremony, and in the biometric case already tried it — the sheet never
  // repeats that work, it picks up where the attempt stopped.
  readonly seed: SheetSeed;
  readonly onGranted: (grant: string) => void;
  readonly onCancel: () => void;
  // Route the user to set up a confirmation method. Omitted where there is
  // nowhere to go.
  readonly onSetUpBiometrics?: () => void;
}

type Phase =
  | { readonly kind: 'STARTING' }
  | {
      readonly kind: 'BIOMETRIC';
      readonly ceremonyId: string;
      readonly alternatives: readonly StepUpMethod[];
    }
  | {
      readonly kind: 'EMAIL';
      readonly ceremonyId: string;
      readonly emailMasked: string;
      readonly alternatives: readonly StepUpMethod[];
      readonly recommendBiometric: boolean;
    }
  | { readonly kind: 'BLOCKED'; readonly message: string };

const phaseFromSeed = (seed: SheetSeed): Phase =>
  seed.kind === 'BLOCKED'
    ? { kind: 'BLOCKED', message: seed.message }
    : seed.started.kind === 'EMAIL'
      ? {
          kind: 'EMAIL',
          ceremonyId: seed.started.ceremonyId,
          emailMasked: seed.started.emailMasked,
          alternatives: seed.started.alternatives,
          recommendBiometric: seed.started.recommendBiometric,
        }
      : {
          kind: 'BIOMETRIC',
          ceremonyId: seed.started.ceremonyId,
          alternatives: seed.started.alternatives,
        };

// Asks for the second factor and hands back a grant. The caller repeats its
// original request with that grant; the bot spends it before the handler runs,
// so one grant authorises exactly one operation.
export const StepUpSheet = ({
  action,
  params,
  summary,
  seed,
  onGranted,
  onCancel,
  onSetUpBiometrics,
}: StepUpSheetProps): ReactNode => {
  const platform = usePlatform();
  const [phase, setPhase] = useState<Phase>(() => phaseFromSeed(seed));
  const [biometrics, setBiometrics] = useState<BiometricState | null>(
    seed.kind === 'STARTED' ? seed.biometrics : null,
  );
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(seed.kind === 'STARTED' ? (seed.error ?? '') : '');
  // Set on unmount so a callback that arrives late does not write into a sheet
  // the user has already closed. Reset on mount too: React mounts, unmounts
  // and remounts in development, and a flag that only ever flipped to true
  // would discard the response to the ceremony the first mount started.
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return (): void => {
      cancelledRef.current = true;
    };
  }, []);

  const begin = useCallback(
    async (method?: StepUpMethod): Promise<void> => {
      setError('');
      setPhase({ kind: 'STARTING' });
      try {
        const [started, state] = await Promise.all([
          startStepUp(action, params, method),
          platform.biometricState(),
        ]);
        if (cancelledRef.current) return;
        setBiometrics(state);
        if (started.kind === 'GRANTED') {
          onGranted(started.grant);
          return;
        }
        if (started.kind === 'EMAIL') {
          setCode('');
          setPhase({
            kind: 'EMAIL',
            ceremonyId: started.ceremonyId,
            emailMasked: started.emailMasked,
            alternatives: started.alternatives,
            recommendBiometric: started.recommendBiometric,
          });
          return;
        }
        setPhase({
          kind: 'BIOMETRIC',
          ceremonyId: started.ceremonyId,
          alternatives: started.alternatives,
        });
      } catch (err: unknown) {
        if (cancelledRef.current) return;
        setPhase({ kind: 'BLOCKED', message: messageFor(err, 'Could not start confirmation.') });
      }
    },
    [action, params, onGranted, platform],
  );

  const confirmWithBiometrics = async (): Promise<void> => {
    if (phase.kind !== 'BIOMETRIC') return;
    setBusy(true);
    setError('');
    try {
      const result = await platform.biometricUnlock(summary);
      if (cancelledRef.current) return;
      if (!result.ok || result.token === '') {
        platform.haptic('error');
        setError('That check did not pass. Try again, or use an email code.');
        return;
      }
      const granted = await verifyStepUp(phase.ceremonyId, {
        biometricToken: result.token,
        deviceId: biometrics?.deviceId ?? '',
      });
      platform.haptic('success');
      onGranted(granted.grant);
    } catch (err: unknown) {
      if (!cancelledRef.current) setError(messageFor(err, 'That check did not pass.'));
    } finally {
      if (!cancelledRef.current) setBusy(false);
    }
  };

  const submitCode = async (): Promise<void> => {
    if (phase.kind !== 'EMAIL') return;
    setBusy(true);
    setError('');
    try {
      const granted = await verifyStepUp(phase.ceremonyId, { code });
      platform.haptic('success');
      onGranted(granted.grant);
    } catch (err: unknown) {
      if (cancelledRef.current) return;
      platform.haptic('error');
      if (err instanceof ApiError && err.code === STEP_UP_CODES.EXPIRED) {
        setPhase({
          kind: 'BLOCKED',
          message: 'That confirmation expired. Start again when you are ready.',
        });
        return;
      }
      setError(messageFor(err, 'That code is not right.'));
      setCode('');
    } finally {
      if (!cancelledRef.current) setBusy(false);
    }
  };

  // The account has a device enrolled, but not this one — the token lives in
  // the device that enrolled it and cannot travel.
  const wrongDevice =
    phase.kind === 'BIOMETRIC' && biometrics !== null && !biometrics.tokenSaved;

  return (
    <>
      <div className="sheet-backdrop open" onClick={onCancel} aria-hidden="true" />
      <div
        className="sheet open step-up-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="step-up-title"
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sheet-header">
          <p className="sheet-title" id="step-up-title">
            Confirm it is you
          </p>
        </div>
        <div className="step-up-body">
          <p className="step-up-summary">{summary}</p>

          {phase.kind === 'STARTING' ? <p className="step-up-note">Preparing…</p> : null}

          {phase.kind === 'BIOMETRIC' && !wrongDevice ? (
            <>
              <p className="step-up-note">
                {error === ''
                  ? `Confirm with your ${biometricLabel(biometrics?.type ?? 'unknown')} on this device.`
                  : 'Try your device again, or use a code emailed to you instead.'}
              </p>
              <button
                type="button"
                className={error === '' ? 'btn-primary' : 'btn-tertiary'}
                disabled={busy}
                onClick={(): void => {
                  void confirmWithBiometrics();
                }}
              >
                {busy ? 'Waiting…' : 'Try again'}
              </button>
            </>
          ) : null}

          {wrongDevice ? (
            <p className="step-up-note">
              This device is not set up yet — the confirmation lives on the device you set up, and
              it cannot be moved. Use an email code here, or set this device up too.
            </p>
          ) : null}

          {phase.kind === 'EMAIL' ? (
            <>
              <p className="step-up-note">We sent a 6-digit code to {phase.emailMasked}.</p>
              <input
                className="step-up-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                aria-label="Confirmation code"
                onChange={(e): void => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                }}
              />
              <button
                type="button"
                className="btn-primary"
                disabled={busy || code.length !== 6}
                onClick={(): void => {
                  void submitCode();
                }}
              >
                {busy ? 'Checking…' : 'Confirm'}
              </button>
              {phase.recommendBiometric ? (
                <p className="step-up-recommend">
                  A code in your inbox is the weakest way to protect money. Set up{' '}
                  {biometricLabel(biometrics?.type ?? 'unknown')} on this device instead — it
                  confirms in one tap, and nothing worth stealing is ever sent anywhere.
                  {onSetUpBiometrics !== undefined && biometrics?.available === true ? (
                    <>
                      {' '}
                      <button type="button" className="link-button" onClick={onSetUpBiometrics}>
                        Set it up
                      </button>
                    </>
                  ) : null}
                </p>
              ) : null}
            </>
          ) : null}

          {phase.kind === 'BLOCKED' ? <p className="step-up-blocked">{phase.message}</p> : null}

          {error !== '' ? (
            <p className="step-up-error" role="alert">
              {error}
            </p>
          ) : null}

          {(phase.kind === 'BIOMETRIC' || phase.kind === 'EMAIL') &&
          phase.alternatives.includes('EMAIL') ? (
            <button
              type="button"
              // The way out is the primary action once the device has already
              // refused: offering "try again" first would be asking the user to
              // repeat what just failed.
              className={phase.kind === 'BIOMETRIC' && error !== '' ? 'btn-primary' : 'link-button'}
              disabled={busy}
              onClick={(): void => {
                void begin('EMAIL');
              }}
            >
              Use an email code
            </button>
          ) : null}
          {phase.kind === 'EMAIL' && phase.alternatives.includes('BIOMETRIC') && !wrongDevice ? (
            <button
              type="button"
              className="link-button"
              disabled={busy}
              onClick={(): void => {
                void begin('BIOMETRIC');
              }}
            >
              Use {biometricLabel(biometrics?.type ?? 'unknown')} instead
            </button>
          ) : null}

          <button type="button" className="btn-tertiary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};
